import React, { useMemo } from 'react';

const safeText = (value, fallback = '-') => {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
};

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const pickFirst = (...values) => values.find((value) => value !== null && value !== undefined && value !== '');

const getScoreValue = (card, side) => {
  const directKey = side === 'home' ? 'homeScore' : 'awayScore';
  return pickFirst(
    card?.[directKey],
    card?.score?.[side],
    card?.scores?.[side],
    card?.liveScore?.[side],
    card?.scoreboard?.[side],
    card?.scores?.[side]?.total,
    card?.teams?.[side]?.score,
    card?.game?.scores?.[side]
  );
};

const getScoreText = (card) => {
  const homeScore = getScoreValue(card, 'home');
  const awayScore = getScoreValue(card, 'away');
  const hasHome = homeScore !== null && homeScore !== undefined && homeScore !== '';
  const hasAway = awayScore !== null && awayScore !== undefined && awayScore !== '';

  if (hasHome && hasAway) return `${homeScore} - ${awayScore}`;
  if (typeof card?.score === 'string' && /\d/.test(card.score)) return card.score;
  if (typeof card?.liveScore === 'string' && /\d/.test(card.liveScore)) return card.liveScore;
  return '-';
};

const getStatusLabel = (card) => {
  const status = safeText(card?.status || card?.matchStatus || card?.gameStatus || card?.state, '').toLowerCase();
  if (status.includes('live') || status.includes('quarter') || status.includes('q')) return 'CANLI';
  return 'CANLI';
};

const getResultLabel = (card) => {
  const result = safeText(card?.resultStatus || card?.predictionStatus, '').toLowerCase();
  if (result.includes('won') || result.includes('win') || result.includes('tuttu')) return { text: 'Tuttu', className: 'won' };
  if (result.includes('lost') || result.includes('loss') || result.includes('tutmad')) return { text: 'Tutmadı', className: 'lost' };
  return { text: 'Beklemede', className: 'pending' };
};

const getConfidenceClass = (confidence) => {
  const score = safeNumber(confidence, 0);
  if (score >= 75) return 'high';
  if (score >= 60) return 'medium';
  return 'low';
};

const getMomentum = (card) => {
  const homeMomentum = safeNumber(pickFirst(card?.homeMomentum, card?.momentum?.home, card?.momentumHome), null);
  const awayMomentum = safeNumber(pickFirst(card?.awayMomentum, card?.momentum?.away, card?.momentumAway), null);

  if (homeMomentum !== null && awayMomentum !== null) {
    return { home: homeMomentum, away: awayMomentum };
  }

  const spread = safeNumber(card?.modelSignal || card?.spreadProjection || card?.spread || card?.edge, 0);
  const confidence = safeNumber(card?.confidence, 50);
  const leanHome = String(card?.selection || '').toLowerCase().includes('ev sahibi') || spread > 0;
  const base = Math.max(45, Math.min(72, 50 + Math.abs(spread) * 2 + (confidence - 60) / 4));

  return leanHome ? { home: Math.round(base), away: Math.round(100 - base) } : { home: Math.round(100 - base), away: Math.round(base) };
};

const getPressureSide = (card, momentum) => {
  const explicit = pickFirst(card?.pressureSide, card?.baski, card?.tempoSide, card?.dominantSide);
  if (explicit) return explicit;

  if (momentum.home === momentum.away) return 'Dengeli';
  return momentum.home > momentum.away ? safeText(card?.homeTeam, 'Ev sahibi') : safeText(card?.awayTeam, 'Deplasman');
};

const getTempoLabel = (card) => {
  const explicit = pickFirst(card?.tempoLabel, card?.tempo, card?.paceLabel);
  if (explicit && !String(explicit).match(/^\d+$/)) return explicit;

  const tempoValue = safeNumber(pickFirst(card?.tempoProjection, card?.pace, card?.tempoScore, card?.tempo), 0);
  if (tempoValue >= 70) return 'Yuksek tempo';
  if (tempoValue >= 45) return 'Orta tempo';
  if (tempoValue > 0) return 'Dusuk tempo';
  return 'Tempo verisi yok';
};

const getSpreadText = (card) => {
  const explicit = pickFirst(card?.spreadText, card?.spreadProjectionText, card?.handicapText);
  if (explicit) return explicit;

  const spread = pickFirst(card?.spreadProjection, card?.modelSignal, card?.spread, card?.edge);
  if (spread === null || spread === undefined || spread === '') return '-';

  const winner = String(card?.selection || '').toLowerCase().includes('deplasman')
    ? safeText(card?.awayTeam, 'Deplasman')
    : safeText(card?.homeTeam, 'Ev sahibi');

  return `${winner} ${Math.abs(safeNumber(spread, 0)).toFixed(1)} fark projeksiyonu`;
};

const BasketballLiveCard = ({ card }) => {
  const result = useMemo(() => getResultLabel(card), [card]);
  const confidenceClass = useMemo(() => getConfidenceClass(card?.confidence), [card]);
  const scoreText = useMemo(() => getScoreText(card), [card]);
  const momentum = useMemo(() => getMomentum(card), [card]);
  const pressureSide = useMemo(() => getPressureSide(card, momentum), [card, momentum]);

  const leagueLine = [card?.league, card?.country].filter(Boolean).join(' / ');
  const confidence = safeNumber(card?.confidence, 0);
  const period = safeText(card?.period || card?.quarter || card?.status, 'Periyot yok');
  const clock = safeText(card?.clock || card?.time || card?.timer, 'Süre yok');

  return (
    <article className="basketball-live-card basketball-live-card--pro">
      <div className="basketball-live-card__top">
        <div>
          <div className="basketball-live-card__league">{safeText(leagueLine, 'Basketbol')}</div>
          <div className="basketball-live-card__date">{safeText(card?.date || card?.matchDate || card?.startTime, '')}</div>
        </div>
        <div className="basketball-live-card__badges">
          <span className="basketball-live-card__status">{getStatusLabel(card)}</span>
          <span className={`basketball-live-card__result basketball-live-card__result--${result.className}`}>{result.text}</span>
        </div>
      </div>

      <div className="basketball-live-card__scoreboard">
        <div className="basketball-live-card__team basketball-live-card__team--home">
          <strong>{safeText(card?.homeTeam)}</strong>
          <span>Ev Sahibi</span>
        </div>
        <div className="basketball-live-card__score basketball-live-card__score--real">
          <strong>{scoreText}</strong>
          <span>{period} · {clock}</span>
        </div>
        <div className="basketball-live-card__team basketball-live-card__team--away">
          <strong>{safeText(card?.awayTeam)}</strong>
          <span>Deplasman</span>
        </div>
      </div>

      <div className="basketball-live-card__pick basketball-live-card__pick--pro">
        <div>
          <span className="basketball-live-card__label">Market</span>
          <strong>{safeText(card?.market || card?.primaryMarket)}</strong>
        </div>
        <div>
          <span className="basketball-live-card__label">Seçim</span>
          <strong>{safeText(card?.selection || card?.primarySelection)}</strong>
        </div>
      </div>

      <div className="basketball-live-card__insight-grid">
        <div><span>Fark Projeksiyonu</span><strong>{getSpreadText(card)}</strong></div>
        <div><span>Baskı</span><strong>{pressureSide}</strong></div>
        <div><span>Tempo</span><strong>{getTempoLabel(card)}</strong></div>
      </div>

      <div className="basketball-live-card__momentum">
        <div className="basketball-live-card__confidence-row">
          <span>Momentum</span>
          <strong>{momentum.home}% - {momentum.away}%</strong>
        </div>
        <div className="basketball-live-card__momentum-bar">
          <span className="basketball-live-card__momentum-home" style={{ width: `${Math.max(0, Math.min(100, momentum.home))}%` }} />
          <span className="basketball-live-card__momentum-away" style={{ width: `${Math.max(0, Math.min(100, momentum.away))}%` }} />
        </div>
        <div className="basketball-live-card__momentum-labels">
          <span>{safeText(card?.homeTeam)}</span>
          <span>{safeText(card?.awayTeam)}</span>
        </div>
      </div>

      <div className="basketball-live-card__confidence">
        <div className="basketball-live-card__confidence-row"><span>Güven</span><strong>%{confidence.toFixed(1)}</strong></div>
        <div className="basketball-live-card__confidence-bar">
          <span className={`basketball-live-card__confidence-fill basketball-live-card__confidence-fill--${confidenceClass}`} style={{ width: `${Math.max(0, Math.min(100, confidence))}%` }} />
        </div>
      </div>

      <div className="basketball-live-card__details">
        <p>{safeText(card?.analysis || card?.reason || card?.summary, 'Canli analiz hazirlaniyor.')}</p>
      </div>

      {Array.isArray(card?.alternatives) && card.alternatives.length > 0 ? (
        <div className="basketball-live-card__alternatives">
          {card.alternatives.slice(0, 3).map((item, index) => (
            <div className="basketball-live-card__alternative" key={`${item?.market || 'alt'}-${index}`}>
              <span>{safeText(item?.market)}</span>
              <strong>{safeText(item?.selection)}</strong>
              <em>%{safeNumber(item?.confidence, 0).toFixed(1)}</em>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
};

export default BasketballLiveCard;
